// Site.styles.ts
//
// Anatomia igual às outras sections de Lojas (Subsidiary, Ambients,
// MeiosPagamento): FieldsContainer como raiz, Header/HeaderContent/
// HeaderTitle/HeaderSubtitle, LoadingContainer/LoadingSpinner, EmptyState.
//
// Tamanhos só na escada de tokens (`var(--sp-N)` / `var(--fs-N)`); o que não cai
// nos valores de --sp- vira `calc(var(--u) * N)`. Fora da escala só borda de
// 1px, traço de SVG e @media. Cores de `props.theme.colors`.
import styled, { keyframes } from "styled-components";
import { ALVO_DE_TOQUE } from "../../../../global/breakpoints";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-24);
  gap: var(--sp-16);
  flex-wrap: wrap;
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  margin: 0;
  font-size: var(--fs-2xl);
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

// ===== CARTÃO DE SEÇÃO =====
export const Cartao = styled.div`
  margin-bottom: var(--sp-20);
  padding: var(--sp-20);
  border-radius: 12px;
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const CartaoTitulo = styled.h3`
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-lg);
  color: ${(props) => props.theme.colors.text};
`;

export const CartaoSubtitulo = styled.div`
  margin-bottom: var(--sp-16);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  line-height: 1.5;
`;

// ===== O EDITOR EM DUAS COLUNAS =====
// Molde: EditorLayoutComanda — coluna de edição à esquerda, preview grudado à
// direita.
//
// A quebra é por CONTAINER QUERY, não por @media, e isso não é preciosismo: a
// mesma tela roda DENTRO do painel de Configuração (que tem uns 700px, mesmo num
// monitor de 1600) e em /settings/site (que tem a página inteira). Com @media, a
// primeira versão abriu duas colunas num container de 700px e a coluna de edição
// ficou com ~60px — uma palavra por linha. Está na foto.
//
// Sem suporte a @container, o padrão é UMA coluna: apertado é pior que empilhado.
//
// ── O BUG QUE ISTO CONSERTA (15/08/2026) ───────────────────────────────────
// `container-type: inline-size` e a consulta `@container` estavam NA MESMA
// regra. Um elemento NÃO consulta o próprio container: a `@container` procura o
// ancestral com `container-type` mais próximo, e não havia nenhum — então ela
// nunca casava e o editor abria SEMPRE empilhado, em qualquer largura, apesar
// de o commit anunciar duas colunas. Sem erro, sem aviso: só a foto errada.
//
// O conserto é separar em dois elementos: quem DECLARA o container e quem o
// CONSULTA. `ColunaPreview`, que também consulta, passa a encontrá-lo.
export const ContainerEditor = styled.div`
  container-type: inline-size;
`;

export const ColunasEditor = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--sp-20);
  align-items: start;

  @container (min-width: 980px) {
    grid-template-columns: minmax(calc(var(--u) * 420), 1fr) minmax(0, calc(var(--u) * 420));
  }
`;

export const ColunaEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
  min-width: 0;
`;

export const ColunaPreview = styled.div`
  min-width: 0;
  position: static;

  /* Grudar só faz sentido quando ele está AO LADO; empilhado, um preview fixo
     tampa o que o dono está editando. */
  @container (min-width: 980px) {
    position: sticky;
    top: var(--sp-16);
  }
`;

// ===== BLOCO NA LISTA DO EDITOR =====
export const BlocoItem = styled.div<{ $selecionado?: boolean }>`
  padding: var(--sp-12) var(--sp-16);
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid
    ${(props) =>
      props.$selecionado
        ? props.theme.colors.selection
        : props.theme.colors.selection + "30"};
  animation: ${fadeIn} 0.2s ease;
`;

export const BlocoCabecalho = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-10);
`;

export const BlocoNome = styled.button`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  text-align: left;
`;

export const BlocoAcoes = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-4);
`;

export const BlocoCampos = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-12);
  margin-top: var(--sp-16);
  padding-top: var(--sp-16);
  border-top: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

// ===== CAMPOS =====
export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
`;

export const CampoAjuda = styled.span`
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
  line-height: 1.4;
`;

const entradaBase = `
  padding: var(--sp-8) var(--sp-10);
  border-radius: 8px;
  font-size: var(--fs-md);
  width: 100%;
  box-sizing: border-box;
`;

export const Entrada = styled.input`
  ${entradaBase}
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
`;

export const AreaTexto = styled.textarea`
  ${entradaBase}
  min-height: calc(var(--u) * 90);
  resize: vertical;
  font-family: inherit;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
`;

export const Seletor = styled.select`
  ${entradaBase}
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
`;

export const LinhaCaixa = styled.label`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
`;

export const Caixa = styled.input`
  width: calc(var(--u) * 18);
  height: calc(var(--u) * 18);
  accent-color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
`;

export const CampoCor = styled.input`
  width: calc(var(--u) * 54);
  height: calc(var(--u) * 34);
  padding: 0;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  border-radius: 8px;
  background: none;
  cursor: pointer;
`;

// ===== LINHA E BOTÕES =====
export const Linha = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  flex-wrap: wrap;
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

export const BotaoIcone = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--u) * 30);
  height: calc(var(--u) * 30);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  background: none;
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.selection + "25"};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

// ===== O ENDEREÇO PÚBLICO =====
export const EnderecoPublico = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  word-break: break-all;
`;

export const Sufixo = styled.span`
  font-weight: 400;
  color: ${(props) => props.theme.colors.label};
`;

// ===== AVISO INLINE =====
// Vive na própria aba, e não no <MessageBox /> do Zustand: o MessageBox não
// está montado na tela de Configuração (só em Login e Register).
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.5;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

// ===== PREVIEW =====
// O preview não desenha nada por conta própria: ele materializa a MESMA lista de
// blocos que o site público renderiza, com a mesma cor da marca. É por isso que
// o catálogo é um módulo compartilhado com hash travado no build.
export const MolduraPreview = styled.div`
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  background-color: #ffffff;
  overflow: hidden;
`;

export const BarraPreview = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-8) var(--sp-12);
  background-color: ${(props) => props.theme.colors.background};
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "30"};
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
`;

export const TelaPreview = styled.div`
  max-height: calc(var(--u) * 620);
  overflow: auto;
  background-color: #ffffff;
  color: #16211c;
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-12);
  padding: var(--sp-40);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: calc(var(--u) * 32);
  height: calc(var(--u) * 32);
  border: 3px solid ${(props) => props.theme.colors.selection + "40"};
  border-top-color: ${(props) => props.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-40);
  text-align: center;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-6xl);
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-lg);
  color: ${(props) => props.theme.colors.text};
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span.attrs({
  className: "material-icons",
})`
  font-size: var(--fs-xl);
  line-height: 1;
`;

// ===== CAMPO DE IMAGEM =====
//
// O editor do site pedia ENDEREÇO de imagem em todo campo de foto — logo, capa,
// galeria, marcas, imagem do link. A ajuda embaixo do campo de Open Graph
// admitia por escrito: "o envio de foto pelo editor ainda não existe". Na
// prática isso empurrava o dono do restaurante para hospedar a foto em outro
// lugar antes de conseguir usar o construtor.
//
// O caminho do arquivo já existia — `POST /midia`, autenticado, que grava no R2
// com a chave só do lado do servidor. O que faltava era a peça de tela.

export const CampoImagem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
`;

export const ImagemLinha = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-10);
`;

/* A miniatura é quadrada e recorta por dentro: foto de capa é panorâmica e
   distorceria se fosse esticada para caber. */
export const ImagemMiniatura = styled.div`
  width: calc(var(--u) * 72);
  height: calc(var(--u) * 72);
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  background-color: ${(props) => props.theme.colors.background};
  display: grid;
  place-items: center;
  overflow: hidden;
  color: ${(props) => props.theme.colors.label};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const ImagemControles = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  min-width: 0;
  flex: 1;
`;

export const ImagemAcoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-8);
`;

/* O <input type="file"> nativo não se estiliza e o texto dele não é traduzível.
   O padrão é escondê-lo e disparar por um <label> — que continua sendo o
   controle de verdade para teclado e leitor de tela, ao contrário de um <div>
   com onClick. */
export const ImagemEntradaArquivo = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const ImagemBotao = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-6);
  min-height: ${ALVO_DE_TOQUE};
  padding: var(--sp-8) var(--sp-14);
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.selection};
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => props.theme.colors.selection + "18"};
  }

  .material-icons {
    font-size: var(--fs-base);
  }
`;

/* O anel de foco mora aqui e não no <span>: quem recebe o foco é o input
   escondido, e sem esta regra a navegação por teclado passa pelo botão de
   enviar foto sem nada aparecer na tela. */
export const ImagemRotulo = styled.label`
  display: inline-flex;

  input:focus-visible + span {
    outline: 2px solid ${(props) => props.theme.colors.selection};
    outline-offset: 2px;
  }
`;
